import { useState } from 'react';
import { useQuery } from '../../hooks/useQuery';
import { api, type PlanVersion, type PlanLineRow } from '../../lib/api';
import { Spinner, ActionButton } from '../ui';
import { StatementView } from '../FpaStatement';
import { PlanPnlCharts } from '../FpaCharts';
import { PlanKpis, DepartmentBreakdown } from '../FpaSummary';

/**
 * Planning v2 Review — the plan's P&L read surface, presented consistently with
 * the rest of the workspace: KPI tiles, the P&L statement, trend charts, a
 * department breakdown, and a compact plan-quality (MAPE/bias) readout. Reuses
 * the existing statement/chart/summary components over the computed lines.
 */

const sectionLabel: React.CSSProperties = { fontSize: 12, fontWeight: 600, padding: '12px 12px 6px', color: 'var(--color-text)' };

export default function PlanReviewSection({ plan, canRecompute, onRecomputed }: { plan: PlanVersion; canRecompute: boolean; onRecomputed: () => void }) {
  const planId = plan.id;
  const blend = !!plan.actuals_through;
  const linesQ = useQuery(() => (blend ? api.fpa.plans.blendedLines(planId) : api.fpa.plans.lines(planId)), [planId, blend]);
  const stmtQ = useQuery(() => api.fpa.statement({ plan: [planId], includeActual: blend }), [planId, blend]);
  const qualityQ = useQuery(() => api.fpa.planQuality(planId), [planId]);
  const deptsQ = useQuery(() => api.fpa.departments(), [planId]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function recompute() {
    setBusy(true); setErr(null);
    try { await api.fpa.plans.recompute(planId); linesQ.refetch(); stmtQ.refetch(); qualityQ.refetch(); onRecomputed(); }
    catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }

  if (linesQ.isLoading) return <Spinner />;
  const lines = (linesQ.data?.lines ?? []) as PlanLineRow[];
  const deptNames: Record<string, string> = Object.fromEntries((deptsQ.data?.departments ?? []).map((d) => [d.id, d.name]));
  const q = qualityQ.data?.overall;

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '8px 12px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, fontWeight: 500 }}>P&amp;L review</span>
        {blend && <span style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>blended — actuals ≤ {plan.actuals_through}</span>}
        {q && q.count > 0 && (
          <span style={{ fontSize: 11, color: 'var(--color-text-muted)', marginLeft: 8 }}>
            Plan quality: MAPE <b style={{ color: 'var(--color-text)' }}>{(q.mape * 100).toFixed(1)}%</b> · bias <b style={{ color: 'var(--color-text)' }}>{(q.bias * 100).toFixed(1)}%</b> <span>({q.count} pts)</span>
          </span>
        )}
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          {err && <span style={{ fontSize: 11, color: 'var(--color-danger, #c0392b)' }}>{err}</span>}
          {canRecompute && <ActionButton variant="primary" onClick={recompute} disabled={busy}>{busy ? 'Recomputing…' : 'Recompute'}</ActionButton>}
        </span>
      </div>

      <div style={{ padding: '0 12px' }}><PlanKpis lines={lines} /></div>

      <div style={sectionLabel}>Statement</div>
      <div style={{ padding: '0 12px 12px', overflowX: 'auto' }}>
        {stmtQ.isLoading ? <Spinner /> : stmtQ.data ? <StatementView statement={stmtQ.data} deptNames={deptNames} /> : null}
      </div>

      <div style={sectionLabel}>Trend</div>
      <div style={{ padding: '0 12px 12px' }}><PlanPnlCharts lines={lines} /></div>

      <div style={sectionLabel}>By department</div>
      <div style={{ padding: '0 12px 16px' }}><DepartmentBreakdown lines={lines} /></div>
    </div>
  );
}
